// Vendors
import { takeLatest, put, call, CallEffect, PutEffect } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

// Slice
import {
  fetchInvoiceListFailure,
  fetchInvoiceListStart,
  fetchInvoiceListSuccess,
  resetInvoiceDetailsStart,
  refreshInvoiceDetailStart,
  postMailDocumentSuccess,
  postMailDocumentFailure,
  postMailDocumentStart,
  postCheckoutDetailsFailure,
  postCheckoutDetailsStart,
  postCheckoutDetailsSuccess,
  postRetryPaymentFailure,
  postRetryPaymentSuccess,
  postRetryPaymentStart,
} from './invoiceSlice';

// Models
import {
  InvoiceResponse,
  SendEmailRequestPayload,
  SendEmailResponse,
  CheckoutDetails,
  CheckoutResponse,
  APIResponse,
  RetryPaymentPayload,
} from '@models/Invoice';
import { InvoiceService } from '@services/invoiceService';

// Sage to fetch invoice list
function* fetchInvoice(): Generator<
  CallEffect<InvoiceResponse> | PutEffect,
  void,
  InvoiceResponse
> {
  try {
    const invoiceList: InvoiceResponse = yield call(InvoiceService.fetchInvoiceList);
    yield put(fetchInvoiceListSuccess(invoiceList));
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed';
    yield put(fetchInvoiceListFailure(errorMessage));
  }
}

// Saga to send mail
function* postMailDocument(
  action: PayloadAction<SendEmailRequestPayload>
): Generator<CallEffect<SendEmailResponse> | PutEffect, void, SendEmailResponse> {
  try {
    const response: SendEmailResponse = yield call(InvoiceService.sendMailDocument, action.payload);
    yield put(postMailDocumentSuccess(response));
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
    yield put(postMailDocumentFailure(errorMessage));
  }
}

// Saga to handle refresh invoice details Flag
function* refreshInvoiceDetailSaga(): Generator<CallEffect<void> | PutEffect, void, void> {
  try {
  } catch (error: unknown) {
    console.info('Failed to reset', error);
  }
}

// Reset on logout
function* logout(): Generator<CallEffect<void> | PutEffect, void, void> {
  try {
    // yield put(resetInvoiceDetails());
  } catch (error: unknown) {
    console.info('Failed to reset', error);
  }
}

// Sage to post checkout details
function* checkout(
  action: PayloadAction<CheckoutDetails>
): Generator<
  CallEffect<APIResponse<CheckoutResponse>> | PutEffect,
  void,
  APIResponse<CheckoutResponse>
> {
  try {
    const checkoutDetails: APIResponse<CheckoutResponse> = yield call(
      InvoiceService.postCheckoutDetails,
      action.payload
    );
    yield put(postCheckoutDetailsSuccess(checkoutDetails));
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed';
    yield put(postCheckoutDetailsFailure(errorMessage));
  }
}

// Sage to retry payment
function* postRetryPayment(
  action: PayloadAction<RetryPaymentPayload>
): Generator<
  CallEffect<APIResponse<CheckoutResponse>> | PutEffect,
  void,
  APIResponse<CheckoutResponse>
> {
  try {
    const checkoutDetails: APIResponse<CheckoutResponse> = yield call(
      InvoiceService.retryPayment,
      action.payload
    );
    yield put(postRetryPaymentSuccess(checkoutDetails));
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed';
    yield put(postRetryPaymentFailure(errorMessage));
  }
}

// Watcher Sagas
export function* watchInvoice(): SagaIterator {
  yield takeLatest(fetchInvoiceListStart.type, fetchInvoice);
  yield takeLatest(resetInvoiceDetailsStart.type, logout);
  yield takeLatest(refreshInvoiceDetailStart.type, refreshInvoiceDetailSaga);
  yield takeLatest(postMailDocumentStart.type, postMailDocument);
  yield takeLatest(postCheckoutDetailsStart.type, checkout);
  yield takeLatest(postRetryPaymentStart.type, postRetryPayment);
}
